import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  IonButton,
  IonCheckbox,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonThumbnail,
  IonToolbar,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  albumsOutline,
  arrowUndoOutline,
  barbellOutline,
  calendarOutline,
  cloudDownloadOutline,
  closeOutline,
  chevronBack,
  chevronForward,
  listOutline,
  refreshOutline,
  searchOutline,
  trashOutline,
} from 'ionicons/icons';

import { describeItem, ItemType } from '../../models/item-type';
import { Usage } from '../../models/sequal.models';
import { DbService } from '../../services/db.service';
import { formatBytes } from '../../utils/format-bytes.util';
import { timeAgo } from '../../../../../common/utils/time-ago.util';
import { StorageLabel } from '../../../../../common/enums/storage-label.enum';
import { ListSectionLabel } from '../../../../../common/enums/list-section-label.enum';
import { SectionSearchComponent } from '../section-search/section-search.component';

/** Page numbers a section's pager shows at once, centred on its current page. */
const PAGER_WINDOW = 5;

/** A window of page numbers around `page`, never past either end. */
function pageWindow(page: number, count: number): number[] {
  const span = Math.min(PAGER_WINDOW, count);
  const first = Math.min(Math.max(1, page - Math.floor(span / 2)), count - span + 1);
  return Array.from({ length: span }, (_, i) => first + i);
}

/** Per-kind icon + section heading, grouped and displayed in this order. */
const KIND_META: Record<ItemType, { icon: string; plural: string }> = {
  [ItemType.Activity]: { icon: 'barbell-outline', plural: 'Activities' },
  [ItemType.Group]: { icon: 'albums-outline', plural: 'Groups' },
  [ItemType.Sequence]: { icon: 'list-outline', plural: 'Sequences' },
  [ItemType.Program]: { icon: 'calendar-outline', plural: 'Programs' },
};
const KIND_ORDER: ItemType[] = [
  ItemType.Activity,
  ItemType.Group,
  ItemType.Sequence,
  ItemType.Program,
];

/**
 * A row this list can render. Superset of the local recycle-bin item so both
 * screens share one component: the cloud screen supplies sizes and in-use state
 * and no `deletedAt` (it lists everything stored, deleted or not), the local
 * screen supplies `deletedAt` and, once known, the bytes each row would free.
 */
export interface DeletableItem {
  kind: ItemType;
  id: string;
  name: string;
  imageMediaId?: string;
  /** Epoch ms; drives the "deleted N ago" line. Omitted on the cloud screen. */
  deletedAt?: number;
  /** Bytes deleting this row would actually free (shared media excluded). */
  exclusiveBytes?: number;
  /** Every byte it reaches. Rendered as "· N shared" when it exceeds the above. */
  totalBytes?: number;
  /** True when another item still uses it — unselectable, with the reason shown. */
  blocked?: boolean;
  /** Capped for display; `usageTotal` is how many there really are. */
  usages?: Usage[];
  usageTotal?: number;
  /** Host-written subtitle, used instead of the derived one. The Failed backups
   *  list explains why an item failed and when — neither a size nor a bin
   *  timestamp can say that. */
  note?: string;
}

interface DeletedItemsGroup {
  kind: ItemType;
  meta: (typeof KIND_META)[ItemType];
  /** The rows on screen: this page of what matches the section's search. */
  items: DeletableItem[];
  query: string;
  page: number;
  pageCount: number;
  pageNumbers: number[];
}

/**
 * The item list shared by every screen that offers a library item grouped by
 * kind with a multi-select mode: the Cloud backup screen's cloud items and
 * Failed backups sections.
 *
 * It decides nothing about what the actions mean — it renders, tracks selection
 * and emits. The differences between the hosts are inputs rather than separate
 * components because grouping, selection and thumbnail loading are the bulk of
 * it, and only the labels, the icons and the consequence change: the cloud list
 * pulls a copy down, shows what each row costs and refuses rows another item
 * still uses; the failed list retries an upload and its second action destroys
 * nothing at all (`deleteIsDestructive`).
 *
 * A host that passes `pageSizes` gets every kind's section paged on its own,
 * at its own size, with its own expandable search in the section heading — the
 * cloud list, which runs to hundreds of rows. A host that passes nothing gets
 * one plain list per kind, as before.
 *
 * The host owns the toolbar buttons (they live in its own header), so select
 * mode is driven through the public `selecting` signal and `enterSelectMode()` /
 * `exitSelectMode()` via a `viewChild`.
 *
 * Thumbnails always resolve from the LOCAL media store. On the cloud list an
 * item whose device copy is gone simply has no blob to show and falls back to its
 * kind icon — correct, and cheaper than fetching cover art we are about to delete.
 */
@Component({
  selector: 'app-deleted-items-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonButton,
    IonCheckbox,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel,
    IonThumbnail,
    IonToolbar,
    SectionSearchComponent,
  ],
  template: `
    @if (items().length === 0) {
      <div class="empty-state">
        <ion-icon name="trash-outline" aria-hidden="true"></ion-icon>
        <p>{{ emptyText() }}</p>
      </div>
    } @else {
      @for (group of groups(); track group.kind) {
        <div class="section-title" [class.is-searching]="openSearch() === group.kind">
          <h2>{{ group.meta.plural }}</h2>
          @if (paged()) {
            <app-section-search
              [label]="SectionLabel.SearchPrefix + ' ' + group.meta.plural.toLowerCase()"
              [query]="group.query"
              [expanded]="openSearch() === group.kind"
              [testId]="'dl-search-' + group.kind"
              [toggleTestId]="'dl-search-toggle-' + group.kind"
              (queryChange)="onQuery(group.kind, $event)"
              (expandedChange)="onSearchExpanded(group.kind, $event)"
            ></app-section-search>
          }
        </div>
        @if (group.items.length === 0) {
          <p class="muted no-match" [attr.data-testid]="'dl-no-match-' + group.kind">{{ SectionLabel.NoMatch }}</p>
        } @else {
          <div class="bin-list surface">
            @for (item of group.items; track item.id) {
              <ion-item
                lines="none"
                [button]="selecting() || openable()"
                detail="false"
                [class.blocked]="item.blocked"
                (click)="onRowTap(item)"
                [attr.data-testid]="'deleted-item'"
              >
                @if (thumbFor(item); as thumb) {
                  <ion-thumbnail slot="start">
                    <img [src]="thumb" [alt]="item.name || 'Untitled'" loading="lazy" />
                  </ion-thumbnail>
                } @else {
                  <ion-icon [name]="group.meta.icon" slot="start" aria-hidden="true"></ion-icon>
                }
                <ion-label>
                  <div class="bin-name">{{ item.name || 'Untitled' }}</div>
                  <p class="muted">{{ subtitleFor(item) }}</p>
                </ion-label>
                @if (selecting()) {
                  <!-- A blocked row can't be actioned, so it can't be selected either. -->
                  <ion-checkbox
                    slot="end"
                    [checked]="isSelected(item)"
                    [disabled]="!!item.blocked"
                    aria-label="Selected"
                  ></ion-checkbox>
                } @else {
                  @if (showRestore()) {
                    <ion-button
                      slot="end"
                      fill="clear"
                      (click)="restoreOne.emit(item); $event.stopPropagation()"
                      [attr.aria-label]="restoreLabel()"
                    >
                      <ion-icon slot="icon-only" [name]="restoreIcon()"></ion-icon>
                    </ion-button>
                  }
                  <ion-button
                    slot="end"
                    fill="clear"
                    [color]="deleteIsDestructive() ? 'danger' : 'medium'"
                    [disabled]="!!item.blocked"
                    (click)="deleteOne.emit(item); $event.stopPropagation()"
                    [attr.aria-label]="deleteLabel()"
                  >
                    <ion-icon slot="icon-only" [name]="deleteIcon()"></ion-icon>
                  </ion-button>
                }
              </ion-item>
            }
          </div>
        }
        @if (group.pageCount > 1) {
          <nav
            class="pager"
            [attr.aria-label]="group.meta.plural + ' ' + SectionLabel.Pages"
            [attr.data-testid]="'dl-pager-' + group.kind"
          >
            <ion-button
              fill="clear"
              size="small"
              [disabled]="group.page === 1"
              [attr.aria-label]="SectionLabel.Previous"
              (click)="goTo(group.kind, group.page - 1)"
              [attr.data-testid]="'dl-prev-' + group.kind"
            >
              <ion-icon slot="icon-only" name="chevron-back" aria-hidden="true"></ion-icon>
            </ion-button>
            @for (n of group.pageNumbers; track n) {
              <ion-button
                size="small"
                [fill]="n === group.page ? 'solid' : 'clear'"
                [attr.aria-current]="n === group.page ? 'page' : null"
                [attr.aria-label]="SectionLabel.Page + ' ' + n"
                (click)="goTo(group.kind, n)"
                [attr.data-testid]="'dl-page-' + group.kind + '-' + n"
              >{{ n }}</ion-button>
            }
            <ion-button
              fill="clear"
              size="small"
              [disabled]="group.page === group.pageCount"
              [attr.aria-label]="SectionLabel.Next"
              (click)="goTo(group.kind, group.page + 1)"
              [attr.data-testid]="'dl-next-' + group.kind"
            >
              <ion-icon slot="icon-only" name="chevron-forward" aria-hidden="true"></ion-icon>
            </ion-button>
          </nav>
        }
      }
    }

    @if (selecting()) {
      <ion-footer class="select-actions">
        <ion-toolbar>
          @if (showRestore()) {
            <ion-button
              slot="start"
              fill="clear"
              [disabled]="selectedCount() === 0"
              (click)="restoreMany.emit(selectedItems())"
            >
              {{ restoreLabel() }} ({{ selectedCount() }})
            </ion-button>
          }
          @if (selectionReclaimBytes() !== null) {
            <!-- Set-based, not the sum of the rows: two selected items sharing a
                 blob each show nothing for it, yet deleting both does free it. -->
            <span slot="start" class="reclaim-note">
              {{ FreesLabel }} {{ formatBytes(selectionReclaimBytes()!) }}
            </span>
          }
          <ion-button
            slot="end"
            fill="clear"
            [color]="deleteIsDestructive() ? 'danger' : 'medium'"
            [disabled]="selectedCount() === 0"
            (click)="deleteMany.emit(selectedItems())"
          >
            {{ deleteLabel() }} ({{ selectedCount() }})
          </ion-button>
        </ion-toolbar>
      </ion-footer>
    }
  `,
  styles: [`
    .empty-state { text-align: center; padding: 48px 16px; color: var(--ion-color-medium); }
    .empty-state ion-icon { font-size: 48px; opacity: 0.5; }
    .bin-name { font-weight: 700; letter-spacing: -0.02em; }
    .muted { color: var(--ion-color-medium); font-size: 0.85rem; }
    /* An item another item still uses can't go — dim it, don't hide it, so the
       reason on its subtitle stays readable. */
    .blocked .bin-name { opacity: 0.6; }
    .reclaim-note { padding-inline-start: 16px; font-size: 0.85rem; color: var(--ion-color-medium); }
    .bin-list { overflow: hidden; }
    .bin-list > ion-item { --background: transparent; }
    .bin-list > ion-item + ion-item { border-top: 1px solid var(--app-card-border, var(--app-border)); }
    ion-thumbnail { --border-radius: 8px; }
    ion-thumbnail img { object-fit: cover; }
    /* Selection is toggled by the item tap; the checkbox is display-only. */
    ion-checkbox { pointer-events: none; }
    .select-actions ion-toolbar { --background: var(--ion-background-color); }
    .section-title { display: flex; align-items: center; gap: 8px; }
    /* The heading absorbs the row's slack, so the search button sits at the far
       end; while a search is open the field takes the slack instead. */
    .section-title h2 { flex: 1 1 auto; min-width: 0; white-space: nowrap; }
    .section-title.is-searching h2 { flex: 0 0 auto; }
    .no-match { margin: 4px 0 12px; }
    .pager {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: 4px;
      margin: 8px 0 4px;
    }
    .pager ion-button { margin: 0; min-width: 36px; }
  `],
})
export class DeletedItemsListComponent {
  private readonly db = inject(DbService);

  readonly items = input<DeletableItem[]>([]);
  readonly emptyText = input('Nothing here.');
  /** Whether the row and the footer offer the undo-shaped action at all. */
  readonly showRestore = input(true);
  /** When true a row tap outside select mode emits `openOne` (the cloud preview). */
  readonly openable = input(false);
  /** Bytes the current selection would free; null hides the footer note. */
  readonly selectionReclaimBytes = input<number | null>(null);

  // The two actions are labelled by the host because the same gesture means
  // different things per screen — put an item back, pull it down from the cloud,
  // retry its upload — and a button that says "Restore" on the failed-backups
  // list would describe none of them.
  readonly restoreLabel = input('Restore');
  readonly restoreIcon = input('arrow-undo-outline');
  readonly deleteLabel = input('Delete');
  readonly deleteIcon = input('trash-outline');
  /** False when the second action destroys nothing — the Failed backups list
   *  only forgets an entry, and a red Delete there would claim otherwise. */
  readonly deleteIsDestructive = input(true);

  readonly restoreOne = output<DeletableItem>();
  readonly deleteOne = output<DeletableItem>();
  readonly restoreMany = output<DeletableItem[]>();
  readonly deleteMany = output<DeletableItem[]>();
  /** Row tapped outside select mode — the host opens a preview. */
  readonly openOne = output<DeletableItem>();
  /** Emitted whenever the selection changes, so the host can price it. */
  readonly selectionChange = output<DeletableItem[]>();

  /**
   * Rows per page for each kind, or null for one unpaged list per kind. When
   * set, every kind's section also gets its own search in its heading. The host
   * picks the sizes: they are a product decision, not a property of a list.
   */
  readonly pageSizes = input<Partial<Record<ItemType, number>> | null>(null);
  protected readonly paged = computed(() => this.pageSizes() !== null);

  /** Per-kind name filter and requested page. */
  private readonly queries = signal<Partial<Record<ItemType, string>>>({});
  private readonly pages = signal<Partial<Record<ItemType, number>>>({});
  /** The one section whose search is open — one at a time, as on the Library. */
  protected readonly openSearch = signal<ItemType | null>(null);

  protected readonly formatBytes = formatBytes;
  protected readonly FreesLabel = StorageLabel.Frees;
  protected readonly SectionLabel = ListSectionLabel;

  /** Multi-select mode, entered from the host page's toolbar. Keyed by "kind:id". */
  readonly selecting = signal(false);
  private readonly selectedKeys = signal<ReadonlySet<string>>(new Set());

  /** Selected items still present in the live feed (stale keys are ignored). */
  readonly selectedItems = computed(() =>
    this.items().filter((item) => this.selectedKeys().has(this.itemKey(item))),
  );
  readonly selectedCount = computed(() => this.selectedItems().length);

  /**
   * Items split into per-kind sections, in KIND_ORDER. A kind with nothing
   * stored has no section; one whose search matches nothing keeps its heading,
   * so the search can be changed or cleared. The page is clamped, so a delete
   * that empties a section's last page lands on the page before it.
   */
  protected readonly groups = computed<DeletedItemsGroup[]>(() => {
    const all = this.items();
    const sizes = this.pageSizes();
    const queries = this.queries();
    const pages = this.pages();
    return KIND_ORDER.flatMap((kind): DeletedItemsGroup[] => {
      const ofKind = all.filter((i) => i.kind === kind);
      if (!ofKind.length) return [];
      const query = queries[kind] ?? '';
      const needle = query.trim().toLowerCase();
      const matching = needle
        ? ofKind.filter((i) => (i.name ?? '').toLowerCase().includes(needle))
        : ofKind;
      const size = sizes?.[kind];
      if (!size) {
        return [{ kind, meta: KIND_META[kind], items: matching, query, page: 1, pageCount: 1, pageNumbers: [1] }];
      }
      const pageCount = Math.max(1, Math.ceil(matching.length / size));
      const page = Math.min(pages[kind] ?? 1, pageCount);
      return [{
        kind,
        meta: KIND_META[kind],
        items: matching.slice((page - 1) * size, page * size),
        query,
        page,
        pageCount,
        pageNumbers: pageWindow(page, pageCount),
      }];
    });
  });

  /** "kind:id" → object URL for a resolved cover thumbnail, once loaded. */
  private readonly thumbUrls = signal<Record<string, string>>({});
  private readonly syncThumbUrls = effect(() => void this.updateThumbUrls(this.items()));

  constructor() {
    // Register every icon this list renders; list-outline (Sequences) is not in
    // the global app.component set, so relying on globals leaves it blank.
    addIcons({
      albumsOutline,
      arrowUndoOutline,
      barbellOutline,
      calendarOutline,
      chevronBack,
      chevronForward,
      cloudDownloadOutline,
      closeOutline,
      listOutline,
      refreshOutline,
      searchOutline,
      trashOutline,
    });
  }

  enterSelectMode(): void {
    this.selecting.set(true);
  }

  exitSelectMode(): void {
    this.selecting.set(false);
    this.selectedKeys.set(new Set());
  }

  /** A section's search changed: filter it and start it from its first page. */
  protected onQuery(kind: ItemType, query: string): void {
    this.queries.update((q) => ({ ...q, [kind]: query }));
    this.pages.update((p) => ({ ...p, [kind]: 1 }));
    this.dropSelectionOf(kind);
  }

  /** Opening one section's search closes and clears any other — a filter still
   *  applied under a closed field looks like missing data. */
  protected onSearchExpanded(kind: ItemType, open: boolean): void {
    const current = this.openSearch();
    if (open && current && current !== kind) this.onQuery(current, '');
    this.openSearch.set(open ? kind : current === kind ? null : current);
  }

  protected goTo(kind: ItemType, page: number): void {
    const group = this.groups().find((g) => g.kind === kind);
    if (!group) return;
    const next = Math.min(Math.max(1, page), group.pageCount);
    if (next === group.page) return;
    this.pages.update((p) => ({ ...p, [kind]: next }));
    this.dropSelectionOf(kind);
  }

  /**
   * A section that pages or searches away drops its own selection and leaves
   * the other sections' alone. Selected rows the reader can no longer see would
   * otherwise stay in the bulk action's count and act on things off screen.
   */
  private dropSelectionOf(kind: ItemType): void {
    const prefix = `${kind}:`;
    const keep = new Set([...this.selectedKeys()].filter((k) => !k.startsWith(prefix)));
    if (keep.size === this.selectedKeys().size) return;
    this.selectedKeys.set(keep);
    this.selectionChange.emit(this.selectedItems());
  }

  /** True when everything currently listed is selected — the host escalates to a
   *  double confirm in that case. */
  isEverythingSelected(): boolean {
    const total = this.items().length;
    return total > 0 && this.selectedCount() === total;
  }

  /** Item tap toggles selection while in select mode, otherwise opens the item
   *  when the host asked for that. A blocked row can't be selected — the delete
   *  would be refused server-side, so offering it would only mislead. */
  protected onRowTap(item: DeletableItem): void {
    if (!this.selecting()) {
      if (this.openable()) this.openOne.emit(item);
      return;
    }
    if (item.blocked) return;
    const next = new Set(this.selectedKeys());
    const key = this.itemKey(item);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.selectedKeys.set(next);
    this.selectionChange.emit(this.selectedItems());
  }

  protected isSelected(item: DeletableItem): boolean {
    return this.selectedKeys().has(this.itemKey(item));
  }

  protected thumbFor(item: DeletableItem): string | undefined {
    return this.thumbUrls()[this.itemKey(item)];
  }

  /**
   * The one grey line under each name. In priority order: why it can't be
   * deleted, then what deleting it frees, then when it was deleted — the most
   * actionable fact first, because the row is one line and the user is choosing
   * what to remove.
   */
  protected subtitleFor(item: DeletableItem): string {
    if (item.blocked) return this.usedInLabel(item);
    if (item.note) return item.note;
    const parts: string[] = [];
    if (item.exclusiveBytes !== undefined) parts.push(this.sizeLabel(item));
    if (item.deletedAt !== undefined) parts.push(`deleted ${this.agoLabel(item.deletedAt)}`);
    return parts.join(' · ');
  }

  /**
   * What deleting this row would free — always prefixed "Frees", because a bare
   * byte count next to an item's name reads as the item's SIZE, and the two are
   * different numbers whenever media is shared.
   *
   * A zero gets words rather than "Frees 0 B": every blob the item holds is held
   * by something else too, which is not a bug and not a dead end — the last item
   * to go claims the space. The shared remainder is named in the other cases for
   * the same reason, so a big group reading as cheap explains itself.
   */
  private sizeLabel(item: DeletableItem): string {
    const exclusive = item.exclusiveBytes ?? 0;
    const shared = Math.max(0, (item.totalBytes ?? exclusive) - exclusive);
    if (exclusive === 0 && shared > 0) {
      return `${StorageLabel.FreesNothingAlone} · ${formatBytes(shared)} ${StorageLabel.Shared}`;
    }
    const frees = `${StorageLabel.Frees} ${formatBytes(exclusive)}`;
    return shared > 0 ? `${frees} · ${formatBytes(shared)} ${StorageLabel.Shared}` : frees;
  }

  /** `used in group "Leg Day", program "8-week base" (+2 more)` — never a bare
   *  "in use", and never a bare name: item names are user-chosen and read as
   *  anything, so a program called "2 days" turns "used in 2 days" into a
   *  deadline (see describeItem).
   *
   *  `usages` is already capped, so the "+N more" count comes from `usageTotal`;
   *  deriving it from the array would silently understate how widely the item is
   *  used, which is the one fact that makes the refusal make sense. */
  private usedInLabel(item: DeletableItem): string {
    const described = (item.usages ?? []).map((u) => describeItem(u.kind, u.name));
    if (!described.length) return StorageLabel.UsedElsewhere;
    const more = Math.max(0, (item.usageTotal ?? described.length) - described.length);
    return `${StorageLabel.UsedIn} ${described.join(', ')}${more ? ` (+${more} more)` : ''}`;
  }

  /** Coarse "N days/hours ago" label from an epoch-ms timestamp. */
  protected readonly agoLabel = timeAgo;

  private itemKey(item: DeletableItem): string {
    return `${item.kind}:${item.id}`;
  }

  /** Resolve+cache a cover-image object URL per item; revoke ones no longer listed. */
  private async updateThumbUrls(items: DeletableItem[]): Promise<void> {
    const live = new Set(items.map((item) => this.itemKey(item)));
    const next = { ...this.thumbUrls() };
    let changed = false;
    for (const key of Object.keys(next)) {
      if (!live.has(key)) {
        URL.revokeObjectURL(next[key]);
        delete next[key];
        changed = true;
      }
    }
    for (const item of items) {
      const key = this.itemKey(item);
      if (!item.imageMediaId || next[key]) continue;
      const blob = await this.db.getMedia(item.imageMediaId);
      if (blob) {
        next[key] = URL.createObjectURL(blob);
        changed = true;
      }
    }
    if (changed) this.thumbUrls.set(next);
  }
}
